import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, useBalance } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import { LENS_ABI, BONDING_CURVE_ROUTER_ABI, NADFUN_CONTRACTS } from '../config/abi';

// DUCK Token Address (from env)
const DUCK_TOKEN_ADDRESS = (import.meta.env.VITE_DUCK_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`;

export function useSwap() {
    const { address, isConnected } = useAccount();

    // Get MON balance
    const { data: monBalance, refetch: refetchMonBalance } = useBalance({
        address,
    });

    // Write contract hook for buying
    const {
        writeContract,
        data: txHash,
        isPending: isWritePending,
        error: writeError,
        reset: resetWrite
    } = useWriteContract();

    // Wait for transaction
    const {
        isLoading: isConfirming,
        isSuccess: isConfirmed,
        error: confirmError
    } = useWaitForTransactionReceipt({
        hash: txHash,
    });

    // Get price quote from Lens
    const getQuote = async (amountIn: string, _isBuy: boolean = true) => {
        if (!amountIn || parseFloat(amountIn) <= 0) {
            return { router: null, amountOut: '0' };
        }

        try {
            // This would be called via useReadContract in component
            return { router: NADFUN_CONTRACTS.BONDING_CURVE_ROUTER, amountOut: '0' };
        } catch (error) {
            console.error('Error getting quote:', error);
            return { router: null, amountOut: '0' };
        }
    };

    // Execute buy transaction
    const executeBuy = async (
        tokenAddress: `0x${string}`,
        amountIn: string,
        minAmountOut: bigint,
        slippagePercent: number = 1
    ) => {
        if (!address || !amountIn) return;

        const deadline = Math.floor(Date.now() / 1000) + 300; // 5 minutes
        const amountInWei = parseEther(amountIn);

        // Calculate min output with slippage
        const minOut = (minAmountOut * BigInt(100 - slippagePercent)) / 100n;

        writeContract({
            address: NADFUN_CONTRACTS.BONDING_CURVE_ROUTER as `0x${string}`,
            abi: BONDING_CURVE_ROUTER_ABI,
            functionName: 'buy',
            args: [{
                amountOutMin: minOut,
                token: tokenAddress, // DYNAMIC instead of hardcoded DUCK_TOKEN_ADDRESS
                to: address,
                deadline: BigInt(deadline),
            }],
            value: amountInWei,
        });
    };

    return {
        isConnected,
        address,
        monBalance: monBalance?.formatted || '0',
        monSymbol: monBalance?.symbol || 'MON',
        getQuote,
        executeBuy,
        txHash,
        isWritePending,
        isConfirming,
        isConfirmed,
        writeError,
        confirmError,
        resetWrite,
        refetchMonBalance,
        executeSell: async (
            tokenAddress: `0x${string}`,
            amountIn: string,
            minAmountOut: bigint,
            slippagePercent: number = 1
        ) => {
            if (!address || !amountIn) return;

            const deadline = Math.floor(Date.now() / 1000) + 300;
            const amountInWei = parseEther(amountIn);
            const minOut = (minAmountOut * BigInt(100 - slippagePercent)) / 100n;

            writeContract({
                address: NADFUN_CONTRACTS.BONDING_CURVE_ROUTER as `0x${string}`,
                abi: BONDING_CURVE_ROUTER_ABI,
                functionName: 'sell',
                args: [{
                    amountIn: amountInWei,
                    amountOutMin: minOut,
                    token: tokenAddress,
                    to: address,
                    deadline: BigInt(deadline),
                }],
            });
        },
        approveToken: async (tokenAddress: `0x${string}`, amount: string) => {
            const amountWei = parseEther(amount);
            writeContract({
                address: tokenAddress,
                abi: [{
                    name: 'approve',
                    type: 'function',
                    stateMutability: 'nonpayable',
                    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
                    outputs: [{ name: '', type: 'bool' }]
                }],
                functionName: 'approve',
                args: [NADFUN_CONTRACTS.BONDING_CURVE_ROUTER, amountWei]
            });
        }
    };
}

// Hook to get price quote from Lens contract
export function usePriceQuote(amountIn: string, isBuy: boolean = true) {
    const amountInWei = amountIn && parseFloat(amountIn) > 0
        ? parseEther(amountIn)
        : 0n;

    const { data, isLoading, error, refetch } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'getAmountOut',
        args: [DUCK_TOKEN_ADDRESS, amountInWei, isBuy],
        query: {
            enabled: amountInWei > 0n,
        },
    });

    const result = data as [string, bigint] | undefined;

    return {
        router: result?.[0] || null,
        amountOut: result?.[1] || 0n,
        amountOutFormatted: result?.[1] ? formatEther(result[1]) : '0',
        isLoading,
        error,
        refetch,
    };
}

// Hook to check if token is graduated (on DEX)
export function useTokenStatus() {
    const { data: isGraduated } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'isGraduated',
        args: [DUCK_TOKEN_ADDRESS],
    });

    const { data: isLocked } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'isLocked',
        args: [DUCK_TOKEN_ADDRESS],
    });

    const { data: progress } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'getProgress',
        args: [DUCK_TOKEN_ADDRESS],
    });

    return {
        isGraduated: isGraduated as boolean | undefined,
        isLocked: isLocked as boolean | undefined,
        progress: progress as bigint | undefined,
        progressPercent: progress ? Number(progress) / 100 : 0, // 10000 = 100%
    };
}
